import React from 'react';
import { Check, ChevronsUpDown, Car as CarIcon, Camera, CalendarRange } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
const STATUS_VARIANT = {
    active: 'default',
    scheduled: 'secondary',
    ended: 'outline',
    terminated: 'destructive',
};
function bucketOf(a) {
    // Only active assignments can be in a live photo period. Terminated, ended,
    // or scheduled assignments may still carry a stale currentPeriodStart from
    // before the status flip, so we must not bucket them as "needs photo".
    if (a.status !== 'active') {
        return 'inactive';
    }
    if (a.currentPeriodStart === null || a.currentPeriodStart === undefined) {
        return 'inactive';
    }
    return a.hasPhotoCurrentPeriod ? 'logged' : 'needs_photo';
}
function initials(name) {
    return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() ?? '')
        .join('');
}
function formatPeriod(start, end) {
    if (!start || !end)
        return null;
    const s = new Date(start);
    const e = new Date(end);
    const opts = { month: 'short', day: 'numeric' };
    return `${s.toLocaleDateString(undefined, opts)} – ${e.toLocaleDateString(undefined, opts)}`;
}
export function AssignmentPicker({ value, onChange, assignments, disabled = false, placeholder = 'Pick an assignment', }) {
    const [open, setOpen] = React.useState(false);
    const selected = React.useMemo(() => assignments.find((a) => a.publicId === value) ?? null, [assignments, value]);
    // Group + sort once. Within each bucket, order by status (active first), then by driver name.
    const grouped = React.useMemo(() => {
        const out = {
            needs_photo: [],
            logged: [],
            inactive: [],
        };
        for (const a of assignments) {
            out[bucketOf(a)].push(a);
        }
        const statusOrder = {
            active: 0,
            scheduled: 1,
            ended: 2,
            terminated: 3,
        };
        for (const bucket of Object.keys(out)) {
            out[bucket].sort((a, b) => {
                const s = statusOrder[a.status] - statusOrder[b.status];
                if (s !== 0)
                    return s;
                return a.driverName.localeCompare(b.driverName);
            });
        }
        return out;
    }, [assignments]);
    return (<Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button type="button" variant="outline" role="combobox" aria-expanded={open} disabled={disabled} className="w-full justify-between h-auto py-2">
                    {selected !== null ? (<SelectedRow assignment={selected}/>) : (<span className="text-muted-foreground">{placeholder}</span>)}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50"/>
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start" side="bottom" sideOffset={6} collisionPadding={16} avoidCollisions>
                <Command filter={(itemValue, search) => {
            // itemValue is the lowercased haystack we set on each CommandItem.
            // cmdk's default does substring; we just normalize whitespace.
            const needle = search.trim().toLowerCase();
            return itemValue.includes(needle) ? 1 : 0;
        }}>
                    <CommandInput placeholder="Search driver, plate, campaign…"/>
                    <CommandList className="max-h-[min(360px,calc(var(--radix-popover-content-available-height)-1rem))]">
                        <CommandEmpty>No matching assignments.</CommandEmpty>

                        {grouped.needs_photo.length > 0 && (<CommandGroup heading="Needs photo this period">
                                {grouped.needs_photo.map((a) => (<AssignmentItem key={a.publicId} assignment={a} selected={a.publicId === value} bucket="needs_photo" onSelect={(id) => {
                    onChange(id);
                    setOpen(false);
                }}/>))}
                            </CommandGroup>)}

                        {grouped.logged.length > 0 && (<CommandGroup heading="Already logged this period">
                                {grouped.logged.map((a) => (<AssignmentItem key={a.publicId} assignment={a} selected={a.publicId === value} bucket="logged" onSelect={(id) => {
                    onChange(id);
                    setOpen(false);
                }}/>))}
                            </CommandGroup>)}

                        {grouped.inactive.length > 0 && (<CommandGroup heading="Not in a photo period">
                                {grouped.inactive.map((a) => (<AssignmentItem key={a.publicId} assignment={a} selected={a.publicId === value} bucket="inactive" onSelect={(id) => {
                    onChange(id);
                    setOpen(false);
                }}/>))}
                            </CommandGroup>)}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>);
}
function SelectedRow({ assignment }) {
    return (<div className="flex items-center gap-3 min-w-0 flex-1">
            <Avatar className="h-8 w-8 rounded-md">
                <AvatarImage src={assignment.driverVehicleImageThumbnailUrl ?? undefined}/>
                <AvatarFallback className="rounded-md text-xs">
                    {initials(assignment.driverName) || <CarIcon className="h-4 w-4"/>}
                </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1 text-left">
                <div className="flex items-center gap-1.5 truncate">
                    <span className="font-medium truncate">{assignment.driverName}</span>
                    {assignment.driverLicensePlate && (<span className="font-mono text-xs text-muted-foreground">
                            · {assignment.driverLicensePlate}
                        </span>)}
                </div>
                <div className="text-xs text-muted-foreground truncate">{assignment.campaignName}</div>
            </div>
            <Badge variant={STATUS_VARIANT[assignment.status]} className="capitalize">
                {assignment.status}
            </Badge>
        </div>);
}
// Assignments whose lifecycle has closed can never accept a new photo —
// rendering them keeps context for staff, but selection is blocked.
function isSelectable(a) {
    return a.status !== 'ended' && a.status !== 'terminated';
}
function AssignmentItem({ assignment, selected, bucket, onSelect }) {
    // The Command-level filter uses this value as the haystack.
    const haystack = [assignment.driverName, assignment.driverLicensePlate ?? '', assignment.campaignName]
        .join(' ')
        .toLowerCase();
    const period = formatPeriod(assignment.currentPeriodStart, assignment.currentPeriodEnd);
    const selectable = isSelectable(assignment);
    return (<CommandItem value={haystack} disabled={!selectable} onSelect={() => {
            if (!selectable)
                return;
            onSelect(assignment.publicId);
        }} className={cn('py-2.5', !selectable && 'cursor-not-allowed opacity-60')}>
            <Avatar className="h-9 w-9 rounded-md shrink-0">
                <AvatarImage src={assignment.driverVehicleImageThumbnailUrl ?? undefined}/>
                <AvatarFallback className="rounded-md text-xs">
                    {initials(assignment.driverName) || <CarIcon className="h-4 w-4"/>}
                </AvatarFallback>
            </Avatar>

            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-medium truncate">{assignment.driverName}</span>
                    {assignment.driverLicensePlate && (<span className="font-mono text-xs text-muted-foreground">
                            · {assignment.driverLicensePlate}
                        </span>)}
                    <Badge variant={STATUS_VARIANT[assignment.status]} className="capitalize text-[10px] py-0">
                        {assignment.status}
                    </Badge>
                    {bucket === 'logged' && (<Badge variant="outline" className="text-[10px] py-0 gap-0.5">
                            <Camera className="h-2.5 w-2.5"/>
                            logged
                        </Badge>)}
                    {!selectable && (<span className="text-[10px] uppercase tracking-wide text-muted-foreground">cannot log</span>)}
                </div>
                <div className="text-xs text-muted-foreground truncate">{assignment.campaignName}</div>
                {bucket === 'needs_photo' && period !== null && (<div className="flex items-center gap-1 text-[11px] text-amber-600 dark:text-amber-400 mt-0.5">
                        <CalendarRange className="h-3 w-3"/>
                        <span>photo missing for {period}</span>
                    </div>)}
            </div>

            <Check className={cn('ml-2 h-4 w-4 shrink-0', selected ? 'opacity-100' : 'opacity-0')}/>
        </CommandItem>);
}
